import { lightingFingerprint } from './lightingSolver.js';
import { modelById } from './constants.js';
import { profileSnapshot } from './standards.js';
import { sceneWalls } from './siteGeometry.js';
import { taskDemands } from './tasks.js';
import { recordingFor } from './recording.js';
import { cameraViews } from './optics.js';
import { mountingFingerprint } from './cameraMounts.js';

// Angle normalization on save can introduce ~1e-14 rounding noise. Preserve
// physical changes while avoiding a false recheck from equivalent float values.
const canonical = value => typeof value === 'number' ? Math.round(value * 1e8) / 1e8 : Array.isArray(value) ? value.map(canonical) : value && typeof value === 'object' ? Object.fromEntries(Object.keys(value).sort().filter(k => value[k] !== undefined).map(k => [k, canonical(value[k])])) : value;
/** Stable assessment identity, not a security signature. Ignore costs and labels. */
export function assessmentFingerprint(S, cam) {
  const r = recordingFor(S, cam);
  const payload = canonical({
    lighting: S.lighting?.scenarios.length ? lightingFingerprint(S) : null,
    mounting: mountingFingerprint(modelById(S, cam.modelId), cam),
    views: cameraViews(S, cam).map(({ cam: c, g }) => [c.x, c.y, c.h, c.pan, c.tilt, g.hfov, g.vfov, g.m.resW, g.m.resH]),
    walls: sceneWalls(S).map(w => [w.x1, w.y1, w.x2, w.y2, w.bottom || 0, w.h, w.thickness ?? 0.12, w.kind, w.roofPts || w.wallPts || null]),
    areas: S.areas.map(a => [a.kind, a.environment || 'indoor', a.purpose || '', a.req || '', a.pts]), zones: S.zones.map(z => [z.req, z.views || 1, z.axis || 0, z.pts]),
    tasks: taskDemands(S).map(d => [d.id, d.req, d.points, d.required, d.speed ?? 1.4, d.minDuration || 0, d.maxYaw ?? (d.req === 'plate' ? S.opt.aPlateI : S.opt.faceYaw ?? 45), d.maxDown ?? (d.req === 'plate' ? S.opt.aPlateT : S.opt.aFace),
      d.minVisible ?? 8 / 9, d.minPx || null, d.targetWidth ?? (d.req === 'plate' ? 0.52 : 0.16), d.targetHeight ?? (d.req === 'plate' ? 0.11 : 0.22), d.maxBlurPx || 0, d.exposureMs || null]),
    requirements: profileSnapshot(S).thresholds, angles: [S.opt.aFace, S.opt.aPlateT, S.opt.aPlateI, S.opt.faceYaw ?? 45], heights: [S.opt.hFace, S.opt.hPlate],
    recording: [r.codec, r.fps, r.bitrateMbps || null, r.exposureMs || null, r.scene || ''] });
  let hash = 2166136261;
  for (const c of JSON.stringify(payload)) hash = Math.imul(hash ^ c.charCodeAt(0), 16777619) >>> 0;
  return 'v1-' + hash.toString(16);
}
export function acceptanceStatus(S, cam) {
  const h = cam.handover || {};
  if (h.status !== 'accepted') return h.status || 'pending';
  return h.assessedFingerprint === assessmentFingerprint(S, cam) ? 'accepted' : 'needs recheck';
}
